// src/app/contexts/auth/Provider.jsx
import { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { AuthContext } from './context';
import {
  initKeycloak,
  isAuthenticated,
  doLogin,
  doLogout,
  getUserProfile,
  refreshToken,
} from 'utils/keycloak';

const initialState = {
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  errorMessage: null,
  user: null,
};

const reducerHandlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },

  LOGIN_REQUEST: (state) => {
    return {
      ...state,
      isLoading: true,
    };
  },

  LOGIN_SUCCESS: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      isLoading: false,
      user,
    };
  },

  LOGIN_ERROR: (state, action) => {
    const { errorMessage } = action.payload;
    return {
      ...state,
      errorMessage,
      isLoading: false,
    };
  },

  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) => {
  const handler = reducerHandlers[action.type];
  if (handler) {
    return handler(state, action);
  }
  return state;
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const handleAuthSuccess = () => {
      const user = getUserProfile();
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: true,
          user,
        },
      });

      // Set up token refresh
      const tokenRefreshInterval = setInterval(() => {
        refreshToken().catch(() => {
          // If token refresh fails, log out
          handleLogout();
        });
      }, 60000); // Check every minute

      return () => clearInterval(tokenRefreshInterval);
    };

    const handleAuthError = (error) => {
      console.error('Auth initialization error:', error);
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    };

    // Initialize Keycloak
    initKeycloak(handleAuthSuccess, handleAuthError);
  }, []);

  const login = () => {
    dispatch({ type: 'LOGIN_REQUEST' });

    try {
      doLogin();
      // Note: The actual login success will be handled by the Keycloak callback
    } catch (err) {
      dispatch({
        type: 'LOGIN_ERROR',
        payload: {
          errorMessage: err.message || 'Failed to login',
        },
      });
    }
  };

  const handleLogout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  const logout = () => {
    handleLogout();
    doLogout();
  };

  // This is the key fix - AuthContext is likely not a component but an object with Provider/Consumer
  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
